import React, { lazy, useEffect, useState } from "react";
import "./App.css";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  Outlet,
} from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import checkAuth from "./app/auth";
import initializeApp from "./app/init";
import { setCredentials, selectCurrentToken } from "./app/authSlice";
import SuspenseContent from "./containers/SuspenseContent";

// Importing pages
const Layout = lazy(() => import("./containers/Layout"));
const Login = lazy(() => import("./pages/Login"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const Register = lazy(() => import("./pages/Register"));

// Initializing different libraries
initializeApp();

// Set up axios interceptors (no redirect — PersistLogin handles silent refresh)
checkAuth();

// Attempts a silent refresh on page reload using the httpOnly refresh cookie
function PersistLogin() {
  const dispatch = useDispatch();
  const token = useSelector(selectCurrentToken);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let active = true;

    const silentRefresh = async () => {
      try {
        const baseUrl =
          process.env.REACT_APP_API_BASE_URL ||
          "https://stagingapi.acadimyst.com/";
        const res = await fetch(
          `${baseUrl}api/v1/en/employee/auth/refresh-token`,
          {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({}),
          },
        );
        if (res.ok) {
          const body = await res.json();
          const newToken = body?.data?.data?.token;
          const maxSessionUntil = body?.data?.data?.maxSessionUntil;
          if (active && newToken) {
            dispatch(setCredentials({ token: newToken, maxSessionUntil }));
          }
        }
      } catch (_) {
        // Silent refresh failed — user will be redirected to login by ProtectedRoute
      } finally {
        if (active) setIsLoading(false);
      }
    };

    if (!token) {
      silentRefresh();
    } else {
      setIsLoading(false);
    }

    return () => {
      active = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return isLoading ? <SuspenseContent /> : <Outlet />;
}

// Guards protected routes — redirects to login if no token in Redux
function ProtectedRoute() {
  const token = useSelector(selectCurrentToken);
  return token ? <Layout /> : <Navigate to="/login" replace />;
}

function App() {
  return (
    <>
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/register" element={<Register />} />

          {/* PersistLogin attempts silent refresh on reload, then guards via ProtectedRoute */}
          <Route element={<PersistLogin />}>
            <Route path="/app/*" element={<ProtectedRoute />} />
          </Route>

          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Router>
    </>
  );
}

export default App;
